import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ITrustmateData } from '@features/productPreview/typescript';
import useInflectedTranslation from '@hooks/useInflectedTranslation';
import containerStyles from '@features/productPreview/components/TrustMateReviews/TrustMateReviews.styles';
import styles from '@features/productPreview/components/TrustMateReviews/TrustMateReviews.module.css';

// Deklaracja typu dla TrustMate
declare global {
  interface Window {
    // biome-ignore lint/suspicious/noExplicitAny: external library type
    TrustMate?: any;
  }
}
interface TrustMateReviewsProps {
  trustmateData: ITrustmateData;
}
const TrustMateReviews = ({
  trustmateData
}: TrustMateReviewsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isClient, setIsClient] = useState(false);
  const {
    getInflectedTranslation
  } = useInflectedTranslation();
  useEffect(() => {
    setIsClient(true);
  }, []);
  useEffect(() => {
    if (!isClient) return;

    // Sprawdź czy TrustMate jest już załadowany
    if (typeof window !== 'undefined' && window.TrustMate) {
      return;
    }

    // Dodaj skrypt TrustMate jeśli nie jest jeszcze załadowany
    const script = document.createElement('script');
    script.src = 'https://trustmate.io/widget/api/fb326a21-0e47-4b30-b7f9-abd380f7c932/script';
    script.defer = true;
    script.async = true;

    // Dodaj parametr product z ID produktu
    script.src += `?product=${trustmateData.id}`;
    document.head.appendChild(script);
    return () => {
      // Usuń skrypt przy odmontowaniu komponentu
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [trustmateData.id, isClient]);
  const modifyHydraGradeValue = (el: Element) => {
    if (!el.getAttribute('data-processed')) {
      const original = el.textContent?.trim();
      if (original && !el.querySelector('[data-modified="true"]')) {
        el.innerHTML = `<span>${original}</span><span data-modified="true">/5</span>`;
        el.setAttribute('data-processed', 'true');
      }
    }
  };
  const fixOpinionGrammar = useCallback((el: Element) => {
    if (!el.getAttribute('data-grammar-fixed')) {
      const original = el.textContent || '';
      // Find number followed by "opinii", "opinie", or "opinia"
      const match = original.match(/\b(\d+)\s+(opinii|opinie|opinia)\b/gi);
      if (match) {
        match.forEach(matchedText => {
          const numberMatch = matchedText.match(/\b(\d+)\b/);
          if (numberMatch) {
            const count = parseInt(numberMatch[1], 10);
            const correctForm = getInflectedTranslation(count, 'opinia');
            const fixed = original.replace(new RegExp(`\\b${count}\\s+(opinii|opinie|opinia)\\b`, 'gi'), `${count} ${correctForm}`);
            if (fixed !== original) {
              el.textContent = fixed;
              el.setAttribute('data-grammar-fixed', 'true');
            }
          }
        });
      }
    }
  }, [getInflectedTranslation]);
  const addParentheses = (el: Element) => {
    const original = el.textContent?.trim();
    if (original && !el.getAttribute('data-modified')) {
      el.textContent = `(${original})`;
      el.setAttribute('data-modified', 'true');
    }
  };
  useEffect(() => {
    // Funkcja do przetwarzania elementów
    const processElements = (container: Element | Document = document) => {
      // Szukaj wszystkich elementów hydra-grade__value
      const gradeElements = container.querySelectorAll('.hydra-grade__value');
      gradeElements.forEach(element => {
        modifyHydraGradeValue(element);
      });

      // Szukaj wszystkich elementów reviews-count i score-platforms
      const reviewElements = container.querySelectorAll('.hydra-grade__reviews-count, .tm-score-platforms');
      reviewElements.forEach(element => {
        fixOpinionGrammar(element);
        addParentheses(element);
      });
    };
    const mutationObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              if (element.matches('.hydra-grade__value')) {
                modifyHydraGradeValue(element);
              } else if (element.matches('.hydra-grade__reviews-count, .tm-score-platforms')) {
                fixOpinionGrammar(element);
                addParentheses(element);
              }
              processElements(element);
            }
          });
        }
      });
    });
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Przetwórz istniejące elementy przy inicjalizacji
    const checkExistingElements = () => {
      processElements();
    };

    // Sprawdź elementy po krótkim opóźnieniu, aby dać czas na załadowanie TrustMate
    const timeouts = [setTimeout(checkExistingElements, 100), setTimeout(checkExistingElements, 500), setTimeout(checkExistingElements, 1000), setTimeout(checkExistingElements, 2000)];
    return () => {
      mutationObserver.disconnect();
      timeouts.forEach(timeout => clearTimeout(timeout));
    };
  }, [fixOpinionGrammar]);

  // Nie renderuj komponentu po stronie serwera
  if (!isClient) {
    return <div ref={containerRef} id="fb326a21-0e47-4b30-b7f9-abd380f7c932" style={containerStyles} className={styles.container} />;
  }
  const gerRoundedRating = (rating: number) => {
    return Math.round(rating * 10) / 10;
  };
  return <>
      {/* Twój własny nagłówek */}
      {trustmateData?.aggregateRating && <div className={styles.customHeader} id="trustmate-reviews">
          <div className={styles.customHeaderContent}>
            <span>Ocena {gerRoundedRating(trustmateData.aggregateRating.ratingValue)}/5</span>
            <span style={{
          marginLeft: 8
        }}>
              ({trustmateData.aggregateRating.reviewCount}{' '}
              {getInflectedTranslation(trustmateData.aggregateRating.reviewCount, 'opinia')})
            </span>
          </div>
        </div>}
      {/* Widget TrustMate */}
      <div ref={containerRef} id="fb326a21-0e47-4b30-b7f9-abd380f7c932" style={containerStyles} className={styles.container} />
    </>;
};
export default observer(TrustMateReviews);